import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import BreadcrumbPath from '../Common/BreadcrumbPath';

interface ConfirmMoveModalProps {
  sourceFolderName: string;
  targetFolderName: string;
  sourceFolderId: string;
  targetFolderId: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Enterprise-grade move folder confirmation modal
 * Compact, standardized design with path visualization
 */
const ConfirmMoveModal: React.FC<ConfirmMoveModalProps> = ({
  sourceFolderName,
  targetFolderName,
  sourceFolderId,
  targetFolderId,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const folders = useSelector((state: any) => state.dropbox.folders);

  // Build folder path from folder hierarchy
  const buildFolderPath = (folderId: string | null): string => {
    if (!folderId) return '/';
    
    const path: string[] = [];
    let currentId = folderId;
    
    // Traverse up the folder tree
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        path.unshift(folder.name);
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    
    return path.length > 0 ? '/' + path.join('/') : '/';
  };

  const sourcePath = buildFolderPath(sourceFolderId);
  const targetPath = buildFolderPath(targetFolderId);

  return (
    <Modal 
      show={true} 
      onHide={onCancel} 
      centered 
      size="sm"
      backdrop="static"
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading} className="border-bottom-0 pb-2">
        <Modal.Title className="h6 mb-0">
          <i className="fas fa-folder-open text-primary mr-2"></i>
          Move Folder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2 pb-3">
        <div className="bg-light border rounded p-2" style={{ fontSize: '0.85rem' }}>
          <div className="mb-2">
            <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
              From
            </small>
            <div className="bg-white border rounded px-2 py-1">
              <BreadcrumbPath path={sourcePath} showIcon={true} />
            </div>
          </div>
          <div>
            <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
              To
            </small>
            <div className="bg-white border rounded px-2 py-1">
              <BreadcrumbPath path={`${targetPath}/${sourceFolderName}`} showIcon={true} />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <Button 
          variant="light" 
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
          className="px-3"
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          size="sm"
          onClick={onConfirm}
          disabled={isLoading}
          className="px-3"
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              Moving...
            </>
          ) : (
            'Move Folder'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmMoveModal;
